/**
 * Cache Control Security Utilities
 *
 * Provides utilities to ensure sensitive HTTPS responses are not cached
 * by intermediaries (CDNs, proxies, browsers).
 *
 * SECURITY: Sensitive endpoints must include Cache-Control: no-store in response headers
 * to prevent caching of sensitive data (tokens, user data, authentication responses).
 */

/**
 * Validates that a response includes proper Cache-Control headers for sensitive data.
 *
 * @param {Response} response - The fetch Response object to validate
 * @param {string} endpoint - The endpoint URL (for logging purposes)
 * @param {boolean} strict - If true, throws error on missing header. If false, logs warning.
 * @returns {boolean} - True if cache control is properly configured
 * @throws {Error} - If strict=true and header is missing
 */
export function validateCacheControlHeader (response, endpoint, strict = false) {
  const cacheControl = response.headers.get('Cache-Control');
  const isNoStore = cacheControl?.toLowerCase().includes('no-store');

  if (!cacheControl) {
    const message = `SECURITY WARNING: Missing Cache-Control header for sensitive endpoint: ${endpoint}`;
    if (strict) {
      throw new Error(message);
    }
    if (process.env.NODE_ENV !== 'production') {
      console.warn(message);
    }
    return false;
  }

  if (!isNoStore) {
    const message = `SECURITY WARNING: Cache-Control header does not include 'no-store' for sensitive endpoint: ${endpoint}. Current value: ${cacheControl}`;
    if (strict) {
      throw new Error(message);
    }
    if (process.env.NODE_ENV !== 'production') {
      console.warn(message);
    }
    return false;
  }

  return true;
}

/**
 * Wraps a fetch call with cache: 'no-store' and optional header validation.
 *
 * @param {string} url - The URL to fetch
 * @param {RequestInit} options - Fetch options (will be merged with cache control)
 * @param {boolean} validateResponse - If true, validates response headers
 * @returns {Promise<Response>} - The fetch response
 */
export async function secureFetch (url, options = {}, validateResponse = false) {
  const secureOptions = {
    ...options,
    cache: 'no-store', // Prevent browser from caching the response
    headers: {
      ...options.headers,
      'Cache-Control': 'no-cache', // Request header to prevent intermediate caching
    },
  };

  const response = await fetch(url, secureOptions);

  if (validateResponse) {
    validateCacheControlHeader(response, url, false);
  }

  return response;
}

/**
 * List of endpoint patterns that are considered sensitive and should never be cached.
 * Used for documentation and validation purposes.
 */
const SENSITIVE_ENDPOINT_PATTERNS = [
  // Authentication endpoints
  '/auth/token/',
  '/auth/token/nonpublic',
  '/auth/token/verify',
  '/user/auth0/',
  '/user/setpassword',
  '/user/mfa',

  // User data endpoints
  '/user/',
  '/users',

  // Password and security endpoints
  '/password',
  '/reset',
  '/mfa',

  // Session and token endpoints
  '/session',
  '/token',

  // Sensitive configuration endpoints
  '/config/',
];

/**
 * Checks if an endpoint URL matches sensitive endpoint patterns.
 *
 * @param {string} url - The endpoint URL to check
 * @returns {boolean} - True if the endpoint is considered sensitive
 */
export function isSensitiveEndpoint (url) {
  if (!url || typeof url !== 'string') {
    return false;
  }

  return SENSITIVE_ENDPOINT_PATTERNS.some((pattern) => url.includes(pattern));
}

